import { apiClient } from '@/lib/api/client';

export interface PaymentApprovalTier {
  maxAmount: number | null;
  requiredApproverRoles: string[];
}

export interface ComplianceCategory {
  name: string;
  regulatoryBody?: string;
  description?: string;
}

export interface TenantSettings {
  id: string;
  tenantId: string;
  defaultCurrency: string;
  unitsOfMeasurement: string;
  timezone: string;
  logoUrl?: string | null;
  organizationName: string;
  registrationNumber?: string | null;
  jurisdiction?: string | null;
  country: string;
  physicalAddress?: string | null;
  onboardingComplete: boolean;
  simplifiedWorkflowMode: boolean;
  shiftDefinitions?: Record<string, string> | null;
  reportBranding?: Record<string, string> | null;
  complianceCategories?: ComplianceCategory[] | null;
  paymentBatchApprovalThresholds?: PaymentApprovalTier[] | null;
  // FR-PAYSEC-005 — batches above this RWF amount require a 2FA/OTP code on
  // approval. Null/omitted → system default.
  paymentOtpThresholdRwf?: number | null;
  // Days an APPROVED, unpaid requisition can sit before the daily ageing
  // alert names it to the Mine Manager and Finance Officer. Null/omitted →
  // system default.
  requisitionAgeingLimitDays?: number | null;
  licenseExpiryWarningDays?: number[];
  commitmentExpiryWarningDays?: number[];
  complianceOverdueThresholdDays?: number;
  misfireThreshold?: number;
  equipmentBreakdownThresholdMinutes?: number;
  // §6.6 / §6.7
  packagingUnitKg?: number;
  meuCodePrefix?: string | null;
  productionCategories?: string[];
  materialClassificationList?: string[];
  devMaterialClassifications?: string[];
  // FR-OPS-010 — production-log activity/ore type options (dropdown + "Other").
  activityTypes?: string[];
  oreTypes?: string[];
  custodyAttendanceCrossRef?: 'WARN' | 'BLOCK';
  // §6.9.3 — FR-CRYPTO-023 enforcement
  requireDigitalSignatures?: boolean;
  // FR-LBR-031 — per-tenant half-day midpoint threshold (hours).
  halfDayThresholdHours?: number | null;
  // FR-LBR-050 — tenant-wide default payment cadence.
  defaultPaymentFrequency?: 'DAILY' | 'WEEKLY' | 'BI_WEEKLY' | 'MONTHLY' | null;
  // FR-WEEKLY-001 — first day of the weekly report grid (1=Mon … 7=Sun).
  weekStartDay?: number;
  // FR-AUD-005 — per-tenant audit-log retention in years (min 5, recommended 7).
  auditLogRetentionYears?: number;
  // FR-BRAND-013 — "From" identity on outgoing tenant email. Null/omitted → platform default.
  emailSenderName?: string | null;
  emailSenderAddress?: string | null;
}

export type UpdateTenantSettingsPayload = Partial<
  Omit<TenantSettings, 'id' | 'tenantId' | 'onboardingComplete'>
>;

export const tenantSettingsService = {
  async get(): Promise<TenantSettings> {
    const { data } = await apiClient.get<TenantSettings>('/tenant-config/settings');
    return data;
  },
  async update(payload: UpdateTenantSettingsPayload): Promise<TenantSettings> {
    const { data } = await apiClient.patch<TenantSettings>(
      '/tenant-config/settings',
      payload,
    );
    return data;
  },
};
